import { Component, For, createSignal, onMount, Show } from 'solid-js';

interface SettingsPanelProps {
  onSave: (apiKey: string, cseId: string) => void;
  onClose: () => void;
  // bring model/theme controls into drawer
  // These are optional; if not provided, the section stays hidden
  modelList?: string[];
  selectedModel?: string;
  onModelSelect?: (model: string) => void;
  onLoadModel?: () => void;
  isLoading?: boolean;
  themeMode?: 'dark' | 'light' | 'system';
  onCycleTheme?: () => void;
  // New: cache management
  onClearCache?: () => Promise<void> | void;
  cacheCount?: number;
  // New: UI toggles for thinking display
  showThinkingDefault?: boolean;
  expandThinkingWhileStreaming?: boolean;
  onToggleShowThinkingDefault?: (value: boolean) => void;
  onToggleExpandThinking?: (value: boolean) => void;
  // New: Search behavior preference
  searchMode?: 'auto' | 'always' | 'never';
  onChangeSearchMode?: (mode: 'auto' | 'always' | 'never') => void;
  // New: System prompt and sampling
  systemPrompt?: string;
  onSystemPromptChange?: (value: string) => void;
  sampling?: {
    temperature?: number;
    maxTokens?: number;
    topP?: number;
    presencePenalty?: number;
    frequencyPenalty?: number;
  };
  onSamplingChange?: (cfg: {
    temperature?: number;
    maxTokens?: number | undefined;
    topP?: number | undefined;
    presencePenalty?: number | undefined;
    frequencyPenalty?: number | undefined;
  }) => void;
}

export const SettingsPanel: Component<SettingsPanelProps> = (props) => {
  const [apiKey, setApiKey] = createSignal('');
  const [cseId, setCseId] = createSignal('');
  const [status, setStatus] = createSignal('');
  const [showApiKey, setShowApiKey] = createSignal(false);
  const [errors, setErrors] = createSignal<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [isClearingCache, setIsClearingCache] = createSignal(false);
  type SamplingCfg = { temperature?: number; maxTokens?: number; topP?: number; presencePenalty?: number; frequencyPenalty?: number };
  const [localSystemPrompt, setLocalSystemPrompt] = createSignal<string>(props.systemPrompt ?? '');
  const [localSampling, setLocalSampling] = createSignal<SamplingCfg>({
    temperature: props.sampling?.temperature ?? 0.7,
    maxTokens: props.sampling?.maxTokens,
    topP: props.sampling?.topP,
    presencePenalty: props.sampling?.presencePenalty,
    frequencyPenalty: props.sampling?.frequencyPenalty,
  });

  let formRef: HTMLFormElement | undefined;
  let firstInputRef: HTMLInputElement | undefined;
  // Section refs for persistence of expanded/collapsed state
  let detMaster: HTMLDetailsElement | undefined;
  let detModelTheme: HTMLDetailsElement | undefined;
  let detWebSearch: HTMLDetailsElement | undefined;
  let detModelCache: HTMLDetailsElement | undefined;
  let detUiPrefs: HTMLDetailsElement | undefined;
  let detSystemPrompt: HTMLDetailsElement | undefined;
  let detGen: HTMLDetailsElement | undefined;

  const saveSectionOpens = () => {
    const payload = {
      master: !!detMaster?.open,
      modelTheme: !!detModelTheme?.open,
      webSearch: !!detWebSearch?.open,
      modelCache: !!detModelCache?.open,
      uiPrefs: !!detUiPrefs?.open,
      systemPrompt: !!detSystemPrompt?.open,
      generation: !!detGen?.open,
    };
    try { localStorage.setItem('settingsSectionsOpen', JSON.stringify(payload)); } catch {}
  };
  
  onMount(() => {
    // Load saved settings
    const saved = localStorage.getItem('webSearchSettings');
    if (saved) {
      try {
        const { googleApiKey, googleCseId } = JSON.parse(saved);
        setApiKey(googleApiKey || '');
        setCseId(googleCseId || '');
      } catch (error) {
        console.error('Error loading saved settings:', error);
      }
    }

    // Focus first input for accessibility
    if (firstInputRef) {
      firstInputRef.focus();
    }

    // Trap focus within the settings panel
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        props.onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    // Restore expanded states
    try {
      const raw = localStorage.getItem('settingsSectionsOpen');
      if (raw) {
        const s = JSON.parse(raw) as Partial<Record<string, boolean>>;
        if (detMaster && typeof s.master === 'boolean') detMaster.open = s.master;
        if (detModelTheme && typeof s.modelTheme === 'boolean') detModelTheme.open = s.modelTheme;
        if (detWebSearch && typeof s.webSearch === 'boolean') detWebSearch.open = s.webSearch;
        if (detModelCache && typeof s.modelCache === 'boolean') detModelCache.open = s.modelCache;
        if (detUiPrefs && typeof s.uiPrefs === 'boolean') detUiPrefs.open = s.uiPrefs;
        if (detSystemPrompt && typeof s.systemPrompt === 'boolean') detSystemPrompt.open = s.systemPrompt;
        if (detGen && typeof s.generation === 'boolean') detGen.open = s.generation;
      }
    } catch {}

    return () => document.removeEventListener('keydown', handleKeyDown);
  });

  // Sync incoming props to local state when panel opens
  // Keep simple: update when props change
  // eslint-disable-next-line solid/reactivity
  if (props.systemPrompt !== undefined && props.systemPrompt !== localSystemPrompt()) {
    setLocalSystemPrompt(props.systemPrompt);
  }
  // eslint-disable-next-line solid/reactivity
  if (props.sampling && JSON.stringify(props.sampling) !== JSON.stringify(localSampling())) {
    setLocalSampling({
      temperature: props.sampling.temperature ?? 0.7,
      maxTokens: props.sampling.maxTokens,
      topP: props.sampling.topP,
      presencePenalty: props.sampling.presencePenalty,
      frequencyPenalty: props.sampling.frequencyPenalty,
    });
  }

  const validateField = (field: string, value: string) => {
    const newErrors = { ...errors() };
    
    switch (field) {
      case 'apiKey':
        if (!value.trim()) {
          newErrors.apiKey = 'API Key is required';
        } else if (value.length < 10) {
          newErrors.apiKey = 'API Key appears to be too short';
        } else {
          delete newErrors.apiKey;
        }
        break;
      case 'cseId':
        if (!value.trim()) {
          newErrors.cseId = 'Custom Search Engine ID is required';
        } else {
          delete newErrors.cseId;
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApiKeyChange = (value: string) => {
    setApiKey(value);
    validateField('apiKey', value);
  };

  const handleCseIdChange = (value: string) => {
    setCseId(value);
    validateField('cseId', value);
  };
  
  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    setIsSubmitting(true);

    const apiKeyValue = apiKey().trim();
    const cseIdValue = cseId().trim();

    // Validate all fields
    const isApiKeyValid = validateField('apiKey', apiKeyValue);
    const isCseIdValid = validateField('cseId', cseIdValue);

    if (!isApiKeyValid || !isCseIdValid) {
      setStatus('❌ Please fix the errors above');
      setIsSubmitting(false);
      setTimeout(() => setStatus(''), 5000);
      return;
    }

    try {
      props.onSave(apiKeyValue, cseIdValue);
      setStatus('✅ Settings saved successfully!');
      setTimeout(() => {
        setStatus('');
        props.onClose();
      }, 2000);
    } catch (error) {
      setStatus('❌ Failed to save settings. Please try again.');
      setTimeout(() => setStatus(''), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setApiKey('');
    setCseId('');
    setErrors({});
    setStatus('');
    localStorage.removeItem('webSearchSettings');
  };

  const handleClearCache = async () => {
    if (!props.onClearCache) return;
    if (!confirm('Clear local model cache? This will remove cached model data and may require re-downloading models.')) {
      return;
    }
    try {
      setIsClearingCache(true);
      await props.onClearCache();
      setStatus('✅ Cache cleared');
      setTimeout(() => setStatus(''), 3000);
    } catch (e) {
      setStatus('❌ Failed to clear cache');
      setTimeout(() => setStatus(''), 5000);
    } finally {
      setIsClearingCache(false);
    }
  };
  
  // Collapsible summaries
  const isSearchConfigured = () => apiKey().trim().length > 0 && cseId().trim().length > 0;
  const searchMeta = () => `${isSearchConfigured() ? 'Configured' : 'Not configured'} • Mode: ${props.searchMode ?? 'auto'}`;
  const uiMeta = () => `Thinking: ${props.showThinkingDefault ? 'Shown' : 'Hidden'} • Expand: ${props.expandThinkingWhileStreaming ? 'On' : 'Off'}`;
  const samplingMeta = () => {
    const s = localSampling();
    const parts = [
      `Temp ${((s.temperature ?? 0.7)).toFixed(2)}`,
      `TopP ${s.topP ?? '—'}`,
      `Max ${s.maxTokens ?? '—'}`
    ];
    return parts.join(' • ');
  };
  const masterMeta = () => `Search: ${isSearchConfigured() ? 'On' : 'Off'} • Cache: ${props.cacheCount ?? 0} • ${uiMeta()} • ${samplingMeta()}`;
  
  return (
    <section 
      id="settings-panel"
      class="settings-panel"
      role="region"
      aria-labelledby="settings-title"
    >
  <header class="settings-header">
        <h2 id="settings-title">
          <span role="img" aria-label="Search settings">🔍</span>
          Web Search Settings
        </h2>
        <button
          type="button"
          class="close-btn"
          onClick={props.onClose}
          aria-label="Close settings panel"
        >
          <span role="img" aria-hidden="true">✕</span>
        </button>
      </header>

  <form ref={formRef} onSubmit={handleSubmit} class="settings-form" noValidate>
        <details ref={detMaster} class="settings-master" onToggle={saveSectionOpens}>
          <summary class="settings-master-summary">
            <h3>Settings</h3>
            <span class="settings-meta">{masterMeta()}</span>
          </summary>
          <div class="settings-content">
          <Show when={(props.modelList?.length ?? 0) > 0}>
            <details ref={detModelTheme} class="settings-section" open onToggle={saveSectionOpens}>
              <summary>
                <h3 class="settings-subtitle">Model & Theme</h3>
              </summary>
              <div class="setting-row">
                <label for="drawer-model">Model</label>
                <select
                  id="drawer-model"
                  value={props.selectedModel ?? ''}
                  onChange={(e) => props.onModelSelect?.(e.currentTarget.value)}
                  disabled={props.isLoading}
                >
                  <option value="" disabled>Select a model…</option>
                  <For each={props.modelList}>{(m) => <option value={m}>{m}</option>}</For>
                </select>
                <button type="button" class="primary" onClick={() => props.onLoadModel?.()} disabled={props.isLoading || !props.selectedModel}>Load</button>
              </div>
              <div class="setting-row">
                <label>Theme</label>
                <button type="button" class="secondary" onClick={() => props.onCycleTheme?.()} title={`Theme: ${props.themeMode ?? 'system'}`} aria-label={`Theme: ${props.themeMode ?? 'system'}`}>
                  {props.themeMode ?? 'system'} (change)
                </button>
              </div>
            </details>
          </Show>
          <details ref={detWebSearch} class="settings-section" open onToggle={saveSectionOpens}>
            <summary>
              <h3 class="settings-subtitle">Web Search</h3>
              <span class="settings-meta">{searchMeta()}</span>
            </summary>
            <div class="onboarding-info">
              <p>
                Enable web search to include current information in AI responses. 
                You'll need a Google API key and Custom Search Engine ID.
              </p>
            </div>
            <div class="form-group">
              <label for="googleApiKey" class="form-label">
                Google API Key
                <span class="required" aria-label="required">*</span>
              </label>
              <div class="input-with-toggle">
                <input
                  ref={firstInputRef}
                  type={showApiKey() ? 'text' : 'password'}
                  id="googleApiKey"
                  value={apiKey()}
                  onInput={(e) => handleApiKeyChange(e.currentTarget.value)}
                  placeholder="AIza..."
                  aria-describedby="api-key-help api-key-error"
                  class={errors().apiKey ? 'error' : ''}
                  autocomplete="off"
                  spellcheck="false"
                  required
                />
                <button
                  type="button"
                  class="toggle-visibility"
                  onClick={() => setShowApiKey(!showApiKey())}
                  aria-label={showApiKey() ? 'Hide API key' : 'Show API key'}
                >
                  <span role="img" aria-hidden="true">
                    {showApiKey() ? '🙈' : '👁️'}
                  </span>
                </button>
              </div>
              <div id="api-key-help" class="field-help">
                Get your API key from the 
                <a 
                  href="https://console.cloud.google.com/apis/credentials" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-describedby="external-link-warning"
                >
                  Google Cloud Console
                </a>
              </div>
              <Show when={errors().apiKey}>
                <div id="api-key-error" class="field-error" role="alert">
                  {errors().apiKey}
                </div>
              </Show>
            </div>

            <div class="form-group">
              <label for="googleCseId" class="form-label">
                Custom Search Engine ID
                <span class="required" aria-label="required">*</span>
              </label>
              <input
                type="text"
                id="googleCseId"
                value={cseId()}
                onInput={(e) => handleCseIdChange(e.currentTarget.value)}
                placeholder="e.g., 017576662512468239146:omuauf_lfve"
                aria-describedby="cse-id-help cse-id-error"
                class={errors().cseId ? 'error' : ''}
                autocomplete="off"
                spellcheck="false"
                required
              />
              <div id="cse-id-help" class="field-help">
                Create a Custom Search Engine at 
                <a 
                  href="https://cse.google.com/cse/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  aria-describedby="external-link-warning"
                >
                  Google Custom Search
                </a>
              </div>
              <Show when={errors().cseId}>
                <div id="cse-id-error" class="field-error" role="alert">
                  {errors().cseId}
                </div>
              </Show>
            </div>

            <div class="form-actions">
              <button 
                type="submit" 
                class="save-btn primary"
                disabled={isSubmitting() || Object.keys(errors()).length > 0}
                aria-describedby="save-help"
              >
                {isSubmitting() ? (
                  <>
                    <span class="loading-spinner" role="img" aria-label="Saving"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <span role="img" aria-hidden="true">💾</span>
                    Save Settings
                  </>
                )}
              </button>
              
              <button
                type="button"
                class="reset-btn secondary"
                onClick={handleReset}
                disabled={isSubmitting()}
              >
                <span role="img" aria-hidden="true">🗑️</span>
                Clear All
              </button>

              <button
                type="button"
                class="cancel-btn secondary"
                onClick={props.onClose}
                disabled={isSubmitting()}
              >
                Cancel
              </button>
            </div>
          </details>

          <details ref={detModelCache} class="settings-section" onToggle={saveSectionOpens}>
            <summary>
              <h3 class="settings-subtitle">Model Cache</h3>
              <span class="settings-meta">Cached: {props.cacheCount ?? 0}</span>
            </summary>
            <div class="onboarding-info">
              <p>Local cache stores model files for faster loading. You can clear it to free space.</p>
            </div>
            <div class="setting-info" aria-live="polite">
              Cached entries: {props.cacheCount ?? 0}
            </div>
            <div class="settings-inline-actions">
              <button
                type="button"
                class="secondary"
                onClick={handleClearCache}
                disabled={isClearingCache() || !props.onClearCache}
              >
                {isClearingCache() ? 'Clearing…' : 'Clear Cache'}
              </button>
            </div>
          </details>

          <details ref={detUiPrefs} class="settings-section" onToggle={saveSectionOpens}>
            <summary>
              <h3 class="settings-subtitle">UI Preferences</h3>
              <span class="settings-meta">{uiMeta()}</span>
            </summary>
            <div class="setting-row">
              <label for="search-mode">Search mode</label>
              <select
                id="search-mode"
                value={props.searchMode ?? 'auto'}
                onChange={(e) => props.onChangeSearchMode?.(e.currentTarget.value as 'auto' | 'always' | 'never')}
              >
                <option value="auto">Auto (heuristic)</option>
                <option value="always">Always</option>
                <option value="never">Never</option>
              </select>
            </div>
            <div class="setting-row">
              <label for="pref-show-thinking">Show thought process by default</label>
              <input
                id="pref-show-thinking"
                type="checkbox"
                checked={!!props.showThinkingDefault}
                onChange={(e) => props.onToggleShowThinkingDefault?.(e.currentTarget.checked)}
              />
            </div>
            <div class="setting-row">
              <label for="pref-expand-while-streaming">Auto-expand during streaming</label>
              <input
                id="pref-expand-while-streaming"
                type="checkbox"
                checked={!!props.expandThinkingWhileStreaming}
                onChange={(e) => props.onToggleExpandThinking?.(e.currentTarget.checked)}
              />
            </div>
          </details>

          <details ref={detSystemPrompt} class="settings-section" onToggle={saveSectionOpens}>
            <summary>
              <h3 class="settings-subtitle">System Prompt</h3>
              <span class="settings-meta">{(localSystemPrompt() || '').length > 0 ? 'Custom' : 'Default'}</span>
            </summary>
            <div class="setting-row">
              <label for="system-prompt">Customize assistant behavior</label>
              <textarea
                id="system-prompt"
                rows={4}
                value={localSystemPrompt()}
                onInput={(e) => setLocalSystemPrompt(e.currentTarget.value)}
                placeholder="You are a helpful AI assistant..."
              />
            </div>
            <div class="setting-row">
              <button
                type="button"
                class="secondary"
                onClick={() => props.onSystemPromptChange?.(localSystemPrompt())}
              >
                Apply system prompt
              </button>
            </div>
          </details>

          <details ref={detGen} class="settings-section" onToggle={saveSectionOpens}>
            <summary>
              <h3 class="settings-subtitle">Generation Settings</h3>
              <span class="settings-meta">{samplingMeta()}</span>
            </summary>
            <div class="setting-row">
              <label for="temp-range">Temperature ({(localSampling().temperature ?? 0.7).toFixed(2)})</label>
              <input
                id="temp-range"
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={localSampling().temperature ?? 0.7}
                onInput={(e) => setLocalSampling({ ...localSampling(), temperature: Number(e.currentTarget.value) })}
              />
            </div>
            <div class="setting-row">
              <label for="max-tokens">Max tokens (blank = model default)</label>
              <input
                id="max-tokens"
                type="number"
                inputMode="numeric"
                min="1"
                placeholder="e.g., 512"
                value={localSampling().maxTokens ?? '' as any}
                onInput={(e) => {
                  const v = e.currentTarget.value.trim();
                  setLocalSampling({ ...localSampling(), maxTokens: v ? Number(v) : undefined });
                }}
              />
            </div>
            <div class="setting-row">
              <label for="top-p">Top P</label>
              <input
                id="top-p"
                type="number"
                inputMode="decimal"
                min="0"
                max="1"
                step="0.01"
                placeholder="e.g., 0.9"
                value={localSampling().topP ?? '' as any}
                onInput={(e) => {
                  const v = e.currentTarget.value.trim();
                  setLocalSampling({ ...localSampling(), topP: v ? Number(v) : undefined });
                }}
              />
            </div>
            <div class="setting-row">
              <label for="presence-pen">Presence penalty</label>
              <input
                id="presence-pen"
                type="number"
                inputMode="decimal"
                step="0.1"
                placeholder="e.g., 0"
                value={localSampling().presencePenalty ?? '' as any}
                onInput={(e) => {
                  const v = e.currentTarget.value.trim();
                  setLocalSampling({ ...localSampling(), presencePenalty: v ? Number(v) : undefined });
                }}
              />
            </div>
            <div class="setting-row">
              <label for="frequency-pen">Frequency penalty</label>
              <input
                id="frequency-pen"
                type="number"
                inputMode="decimal"
                step="0.1"
                placeholder="e.g., 0"
                value={localSampling().frequencyPenalty ?? '' as any}
                onInput={(e) => {
                  const v = e.currentTarget.value.trim();
                  setLocalSampling({ ...localSampling(), frequencyPenalty: v ? Number(v) : undefined });
                }}
              />
            </div>
            <div class="form-actions">
              <button
                type="button"
                class="primary"
                onClick={() => props.onSamplingChange?.(localSampling())}
              >
                Apply generation settings
              </button>
            </div>
          </details>

          <Show when={status()}>
            <div 
              class={`status-message ${status().includes('✅') ? 'success' : 'error'}`}
              role="alert"
              aria-live="assertive"
            >
              {status()}
            </div>
          </Show>
          </div>
        </details>
      </form>

      <div id="save-help" class="visually-hidden">
        Save your Google API credentials to enable web search functionality
      </div>

      <div id="external-link-warning" class="visually-hidden">
        This link opens in a new tab to an external Google service
      </div>
    </section>
  );
};